//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { ContactSupport } from '@campaigns/components/shared/ContactSupport';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { Alert } from '@components/atoms/alert/Alert';
import { BodyText } from '@components/atoms/text/Text';
import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import { Modal } from '@components/molecules/modal/Modal';

interface RequestCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export const RequestCampaignModal = ({
  isOpen,
  onClose,
  onSubmit,
}: RequestCampaignModalProps) => {
  const { isRequesting, isSaveError, updateFormValues, watchFormValues } =
    useSamplingCampaign();

  const formValues = watchFormValues();

  const handleCommentChange = (value: string) => {
    updateFormValues('comments', {
      ...formValues.comments,
      submission: value,
    });
  };

  return (
    <Modal
      closeModal={onClose}
      contents={
        <Container>
          {isSaveError && (
            <Alert type='error'>
              We couldn't send your campaign request. Please try again.{' '}
              <ContactSupport
                leadingCopy='If the problem continues, email'
                requestContext={{
                  campaignName: formValues.name,
                  requestShortCode: formValues.externalId,
                }}
                trackingId={
                  SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_CAMPAIGN_MODAL_CONTACT_SUPPORT_LINK
                }
              />
              .
            </Alert>
          )}
          <BodyText>Everything looks good to us!</BodyText>
          <TextAreaInput
            label='Final note for our team (optional)'
            onChange={handleCommentChange}
            value={formValues.comments?.submission}
          />
        </Container>
      }
      isFooterEnabled
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: 'Send Request',
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_CAMPAIGN_MODAL_SUBMIT_BUTTON,
        onClick: onSubmit,
        isDisabled: isRequesting,
      }}
      tertiaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: onClose,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_CAMPAIGN_MODAL_CANCEL_BUTTON,
        isDisabled: isRequesting,
      }}
      title='Request Campaign'
      width='wide'
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;
